//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type AxiosProgressEvent } from 'axios';
import { type ApiClient } from './apiClient';
import {
  type UploadStatusResult, type UploadInProgress
} from './apiTypes';

/**
 * Data-upload operations: file, link, and SQL uploads, schema inference for an
 * upload, and the upload-status poll.
 */
export class UploadService {
  constructor(private readonly api: ApiClient) {}

  inferFileSchema = (filedata: unknown): Promise<string> => {
    const data = {
      filedata: filedata,
    };

    return this.api.makeRequest<string>('infer_file_schema', 'post', data);
  };

  uploadFile = (
    data: FormData,
    onUploadProgress:
      ((progressEvent: AxiosProgressEvent) => void) | null = null,
    signal: AbortSignal | null = null
  ): Promise<string | UploadStatusResult | UploadInProgress> => {
    // Block responses carry the same in-progress shape as upload_status when
    // live job progress is available (CSV loads run while the blocks stream).
    return this.api.makeRequest<
      string | UploadStatusResult | UploadInProgress>(
      'upload', 'put', data, false, onUploadProgress,
      { 'Content-Type': 'multipart/form-data' },
      false, false, signal);
  };

  uploadLink = (
    data: FormData,
    onUploadProgress:
      ((progressEvent: AxiosProgressEvent) => void) | null = null
  ): Promise<string> => {
    return this.api.makeRequest<string>(
      'upload_link', 'put', data, false, onUploadProgress,
      { 'Content-Type': 'multipart/form-data' });
  };

  uploadSql = (
    data: FormData,
    onUploadProgress:
      ((progressEvent: AxiosProgressEvent) => void) | null = null
  ): Promise<string> => {
    return this.api.makeRequest<string>(
      'upload_sql', 'put', data, false, onUploadProgress,
      { 'Content-Type': 'multipart/form-data' });
  };

  uploadStatus = ():
      Promise<string | UploadStatusResult | UploadInProgress> => {
    return this.api.makeRequest<
      string | UploadStatusResult | UploadInProgress>('upload_status', 'get');
  };
}
